import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../contexts/OrderContext';
import styles from '../styles/GenresPage.module.css';
import { Helmet } from 'react-helmet';

const GenresPage = () => {
  const navigate = useNavigate();
  const { currentOrder, updateOrderDetails, getMaxGenres } = useOrder();
  const [selectedGenres, setSelectedGenres] = useState(currentOrder.genres || []);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [hoveredGenre, setHoveredGenre] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!currentOrder.package) {
      navigate('/packages');
    }
  }, [currentOrder.package, navigate]);

  const maxGenres = getMaxGenres();

  const genres = [
    { name: 'Country', description: 'Heartfelt lyrics with acoustic guitars, fiddles, and warm vocal storytelling.', samplePath: '/audio/country-sample.mp3', color: '#F59E0B', icon: '🎸' },
    { name: 'Classic Rock', description: 'Powerful electric guitars, drums, and iconic vocals reminiscent of the 60s-80s.', samplePath: '/audio/classic-rock-sample.mp3', color: '#EF4444', icon: '🤘' },
    { name: 'Pop', description: 'Catchy melodies, contemporary sounds, and upbeat rhythms.', samplePath: '/audio/pop-sample.mp3', color: '#EC4899', icon: '💃' },
    { name: 'R&B', description: 'Smooth vocals, soulful instrumentals, and emotional depth.', samplePath: '/audio/rnb-sample.mp3', color: '#8B5CF6', icon: '🎤' },
    { name: 'Folk', description: 'Storytelling through acoustic instruments and authentic vocals.', samplePath: '/audio/folk-sample.mp3', color: '#10B981', icon: '🌿' },
    { name: 'Electronic', description: 'Modern beats, synthesizers, and innovative sound design.', samplePath: '/audio/electronic-sample.mp3', color: '#3B82F6', icon: '⚡️' },
    { name: 'Jazz', description: 'Improvisation, syncopated rhythms, and rich harmonies with expressive solos.', samplePath: '/audio/jazz-sample.mp3', color: '#F97316', icon: '🎷' },
    { name: 'Hip-Hop', description: 'Rhythmic vocals, beats, and samples with emphasis on wordplay and flow.', samplePath: '/audio/hip-hop-sample.mp3', color: '#6366F1', icon: '🎧' },
    { name: 'Rap', description: 'Spoken word delivery over beats with focus on lyrical storytelling and rhythm.', samplePath: '/audio/rap-sample.mp3', color: '#14B8A6', icon: '🔥' },
  ];

  const handleSamplePlay = (e, genreName, samplePath) => {
    e.stopPropagation();
    if (playingAudio === genreName) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      audioRef.current.src = samplePath;
      audioRef.current.play().catch(err => console.error(`Error playing ${genreName} sample:`, err));
      setPlayingAudio(genreName);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handleAudioEnded = () => {
    setPlayingAudio(null);
  };

  const handleGenreSelect = (genre) => {
    setSelectedGenres(prevGenres => {
      let newGenres;
      if (prevGenres.includes(genre)) {
        newGenres = prevGenres.filter(g => g !== genre);
      } else {
        if (prevGenres.length >= maxGenres) {
          newGenres = [...prevGenres.slice(1), genre];
        } else {
          newGenres = [...prevGenres, genre];
        }
      }
      setShowNotification(newGenres.length > 0);
      return newGenres;
    });
  };

  const handleContinue = () => {
    if (selectedGenres.length > 0) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setShowNotification(false);
      updateOrderDetails('genres', selectedGenres);
      navigate('/details');
    }
  };

  const getInstructionText = () => {
    const baseText = "Click each style to hear a preview and select ";
    if (maxGenres === 1) {
      return `${baseText} 1 style for your package`;
    }
    return `${baseText} up to ${maxGenres} styles for your package`;
  };

  return (
    <div className={styles.genresContainer}>
      <Helmet>
      <title>Choose Your Music Style | Custom Song Creation | MakeMeSongs</title>
      <meta name="description" content="Select your favorite music styles for your custom song. From Country and Classic Rock to Pop, R&B, Folk, Electronic, Jazz, Hip-Hop, and Rap - hear previews and pick your perfect sound." />
      <meta name="keywords" content="custom song genres, music styles, Country music, Classic Rock, Pop songs, R&B, Folk, Electronic, Jazz, Hip-Hop, Rap, custom music" />
      <meta property="og:title" content="Choose Your Music Style | MakeMeSongs" />
      <meta property="og:description" content="Listen to previews and select your perfect music style. Create a personalized song in Country, Classic Rock, Pop, R&B, Folk, Electronic, Jazz, Hip-Hop, or Rap." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://makemesongs.com/styles" />
      <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
      <link rel="canonical" href="https://makemesongs.com/styles" />
    </Helmet>
      <div className={styles.header}>
        <h1 className={styles.title}>🎵 Choose Your Music Style</h1>
        <p className={styles.instructionMain}>
          You have selected {selectedGenres.length} of {maxGenres} styles for your {currentOrder.package} Package
          <span className={styles.selectionCount}>({selectedGenres.length}/{maxGenres})</span>
        </p>
        <p className={styles.instructionSub}>{getInstructionText()}</p>
      </div>

      <div className={styles.topNavigation}>
        <button className={styles.backButton} onClick={() => navigate('/packages')}>
          Back to Packages
        </button>
        <button
          className={styles.continueButton}
          onClick={handleContinue}
          disabled={selectedGenres.length === 0}
        >
          Continue to Details
        </button>
      </div>

      <div className={styles.genresGrid}>
        {genres.map((genre, index) => (
          <div
            key={genre.name}
            className={`${styles.genreCard} ${selectedGenres.includes(genre.name) ? styles.selected : ''} ${
              hoveredGenre === genre.name ? styles.hovered : ''
            }`}
            onClick={() => handleGenreSelect(genre.name)}
            onMouseEnter={() => setHoveredGenre(genre.name)}
            onMouseLeave={() => setHoveredGenre(null)}
            style={{ '--genre-color': genre.color, animationDelay: `${index * 0.1}s` }}
          >
            <div className={styles.selectionContainer}>
              <span className={styles.selectLabel}>Select Style</span>
              <input
                type="radio"
                className={styles.radioButton}
                checked={selectedGenres.includes(genre.name)}
                onChange={() => handleGenreSelect(genre.name)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className={styles.genreHeader}>
              <span className={styles.genreIcon}>{genre.icon}</span>
              <h2>{genre.name}</h2>
            </div>
            <div className={styles.contentContainer}>
              <p>{genre.description}</p>
            </div>
            <button
              className={`${styles.sampleButton} ${playingAudio === genre.name ? styles.playing : ''}`}
              onClick={(e) => handleSamplePlay(e, genre.name, genre.samplePath)}
            >
              {playingAudio === genre.name ? (
                <svg className={styles.pauseIcon} viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg className={styles.playIcon} viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>
            <input
              type="radio"
              className={styles.mobileRadioButton}
              checked={selectedGenres.includes(genre.name)}
              onChange={() => handleGenreSelect(genre.name)}
              onClick={(e) => e.stopPropagation()}
            />
            {selectedGenres.includes(genre.name) && (
              <div className={styles.selectedOverlay}>Selected</div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.bottomNavigation}>
        <button className={styles.backButton} onClick={() => navigate('/packages')}>
          Back to Packages
        </button>
        <button
          className={styles.continueButton}
          onClick={handleContinue}
          disabled={selectedGenres.length === 0}
        >
          Continue to Details
        </button>
      </div>

      {showNotification && (
        <div className={styles.notification}>
          <p>Genres selected! Click "Continue to Details" to proceed.</p>
          <button
            className={styles.notificationButton}
            onClick={handleContinue}
            disabled={selectedGenres.length === 0}
          >
            Continue to Details
          </button>
        </div>
      )}

      <audio ref={audioRef} onEnded={handleAudioEnded} />
    </div>
  );
};

export default GenresPage;